import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TaskInput, TaskPriority, TaskStatus } from "@sales-ops-amplify/shared";
import {
  bulkUpdateTasks,
  completeTask,
  createTask,
  listTasks,
  skipTask,
  updateTask,
  type ListTasksParams,
} from "@/api/tasks";

export function useTasks(params?: ListTasksParams) {
  return useQuery({
    queryKey: ["tasks", params],
    queryFn: () => listTasks(params),
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaskInput) => createTask(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { taskId: string; notes?: string }) =>
      completeTask(args.taskId, args.notes),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useSkipTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => skipTask(taskId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      taskId: string;
      patch: Partial<TaskInput> & { status?: TaskStatus };
    }) => updateTask(args.taskId, args.patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useBulkTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      taskIds: string[];
      action: "complete" | "reschedule" | "priority" | "skip";
      dueAt?: string;
      priority?: TaskPriority;
    }) => bulkUpdateTasks(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}
